import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { insurersService } from '@/services/insurers';
import type {
  BrokerInsurerOption,
  BrokerInsurerRow,
  InsurerConnectionRequest,
} from '@/services/insurers';

/**
 * The broker's OWN insurance companies — the one lookup any broker-facing
 * filter, dropdown or picker should use.
 *
 * `useInsurersLookup` (hooks/useInsurers.ts) is the country admin's catalogue:
 * every insurer on the platform, and a broker cannot even call it. This one is
 * scoped on the server to a single broker's ACTIVE `BrokerInsurerRelation`
 * rows.
 *
 * **`brokerId` is the BrokerSwitcher's selection and it matters.** A broker-side
 * user can belong to several brokers while `user.tenant_id` pins exactly one of
 * them, and switching does not change it — so leaving this out means a switched
 * user filters by the wrong tenant's insurers. Pass
 * `loginType === 'broker' ? (tenantContext as BrokerContext | null)?.id : undefined`
 * with `user.tenant_id` as the fallback, the same expression the commissions
 * page already uses for its running balance. A broker the caller does not
 * belong to is REFUSED by the server (403), never silently answered with the
 * pinned broker's list.
 *
 * NOTE: `hooks/useBrokerInsuranceCompanies.ts` calls the same endpoint but
 * takes its `brokerId` argument and ignores it (`_brokerId`), so it is not
 * switcher-aware. Prefer this hook; that one should fold into it.
 */
export function useBrokerInsurers(brokerId?: string) {
  return useQuery<BrokerInsurerOption[]>({
    queryKey: ['broker-insurers', brokerId ?? 'me'],
    queryFn: () => insurersService.brokerInsurers(brokerId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Every active insurer in the broker's country, each carrying this broker's
 * relation status, its open connection request and its policy counts.
 *
 * Heavier than `useBrokerInsurers` (it aggregates per-insurer policy counts),
 * so use that one for a dropdown and this one for the Insurer Management
 * screen.
 */
export function useBrokerInsurerManagement(brokerId?: string) {
  return useQuery<BrokerInsurerRow[]>({
    queryKey: ['broker-insurer-management', brokerId ?? 'me'],
    queryFn: () => insurersService.brokerInsurerManagement(brokerId),
  });
}

/** Ask the country admin to connect this broker to an insurer. */
export function useRequestInsurerConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      insurerId,
      note,
      brokerId,
    }: {
      insurerId: string;
      note?: string;
      brokerId?: string;
    }) => insurersService.requestInsurerConnection(insurerId, note, brokerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broker-insurer-management'] });
      queryClient.invalidateQueries({ queryKey: ['broker-insurers'] });
    },
  });
}

/** Country admin: brokers waiting to be connected. Pending unless told otherwise. */
export function useInsurerConnectionRequests(params?: { status?: string; broker?: string }) {
  return useQuery<InsurerConnectionRequest[]>({
    queryKey: ['insurer-connection-requests', params ?? {}],
    queryFn: () => insurersService.listInsurerConnectionRequests(params),
  });
}

/**
 * Country admin: the verdict.
 *
 * Approving is what creates the ACTIVE `BrokerInsurerRelation` — the broker has
 * no path of its own to one, which is the whole point of the request.
 */
export function useDecideInsurerConnectionRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      reason,
    }: {
      id: string;
      decision: 'approve' | 'decline';
      reason?: string;
    }) =>
      decision === 'approve'
        ? insurersService.approveInsurerConnectionRequest(id)
        : insurersService.declineInsurerConnectionRequest(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurer-connection-requests'] });
      queryClient.invalidateQueries({ queryKey: ['broker-insurer-management'] });
    },
  });
}
